import React from "react";
import SeoLandingLayout from "./SeoLandingLayout";
import { Feather, Sparkles, Map as MapIcon, Users, BookOpen, ScrollText } from "lucide-react";
import { NARRATOR_LIST } from "@/data/models";

const FEATURES = [
  { icon: Feather, title: "AI co-writer, novelist voice", body: "The narrator writes in literary prose — sensory detail, dialogue in character, novel-quality pacing." },
  { icon: BookOpen, title: "Chapter-by-chapter drafting", body: "Outline a hero and setting; the AI drafts each chapter around your prompts. Rewind and redo any beat that doesn't land." },
  { icon: Sparkles, title: "Memory Codex for long books", body: "Named characters, factions, threads and items are tracked across turns to help long books stay consistent." },
  { icon: MapIcon, title: "Auto-illustrated scenes", body: "Every major beat gets its own scene illustration. Your finished manuscript ends up illustrated end-to-end." },
  { icon: Users, title: "Multiple narrators", body: `${NARRATOR_LIST}. Switch mid-book without breaking continuity.` },
  { icon: ScrollText, title: "Publish your world", body: "When your book is done, publish the setting to the community library so other writers can build inside it." },
];

const FAQ = [
  {
    q: "Can TalesTalesTales replace a writing partner?",
    a: "It can carry a lot of the weight — drafting chapters, keeping continuity, sketching NPCs, illustrating scenes — but the taste is still yours. Writers use it as an accelerator: the AI drafts, you steer and edit."
  },
  {
    q: "Do I own what I write?",
    a: "You own your saga and your custom worlds. Standard copyright and terms-of-service rules apply to AI-generated text (which are still evolving in most jurisdictions in 2026) — treat AI-drafted prose the way you'd treat any collaborator's contribution."
  },
  {
    q: "How does the Memory Codex help fantasy writers?",
    a: "Fantasy novels lean heavily on continuity. If your hero swore an oath in chapter 2, you want chapter 40 to remember it. The Memory Codex records named characters, factions, threads and inventory changes as they happen, outside the narrator's context window, and feeds that record back to the narrator each chapter to improve continuity — it does not guarantee perfect recall over very long books."
  },
  {
    q: "Can I write in a genre other than fantasy?",
    a: "Yes. The featured worlds are heavily fantasy-leaning (medieval, gothic, wuxia) but the campaign creator accepts any setting you can describe — cyberpunk, weird west, space opera, romance, noir. The narrator adapts tone and vocabulary."
  },
  {
    q: "Can I export the book?",
    a: "Every saga saves in your account and stays readable forever. Chapters are copy-friendly for your own archive. Native EPUB export is on the roadmap."
  },
];

export default function AiFantasyNovelWriter() {
  return (
    <SeoLandingLayout
      testId="seo-ai-fantasy-novel-writer-page"
      path="/ai-fantasy-novel-writer"
      title="AI Fantasy Novel Writer | TalesTalesTales"
      description="An AI fantasy novel writer that keeps continuity across long books. Draft illustrated chapters with switchable AI narrators, a persistent Memory Codex, and community publishing."
      eyebrow="AI Fantasy Novel Writer"
      h1="AI Fantasy Novel Writer"
      h1Accent="that helps keep your book consistent"
      lede="TalesTalesTales is an interactive AI Novel used by fantasy writers as an AI co-writer. Draft illustrated chapters, switch narrators mid-book, and lean on a Memory Codex that helps keep the saga consistent. Standard novel workflow, novel-quality prose."
      body={(
        <>
          <p>
            The reason many AI writing tools struggle on <strong>novel-length work</strong> is
            continuity — the model can forget the oath, the faction, the family history you set up in
            chapter 3 by the time you reach chapter 30. TalesTalesTales helps with this using a{" "}
            <strong>Memory Codex</strong> that lives outside the narrator&rsquo;s
            context window. Named characters, factions, inventory items and milestones
            are recorded as they happen and fed back to the narrator on later chapters — improving
            continuity, though it does not guarantee perfect recall over very long books.
          </p>
          <p>
            The prose is the point. TalesTalesTales narrators write in <strong>literary voice</strong>
            — sensory detail, dialogue in each character&rsquo;s cadence, and novel-length pacing.
            Claude Sonnet 4.5 is the default; Claude Haiku 4.5, GPT-5, and Gemini 2.5 Pro are each
            one click away. Each family has a distinct tonal register — try a few and settle on the
            voice that fits your book. You can switch narrators mid-book if you want a different
            register for a new arc.
          </p>
          <p>
            Every major beat is <strong>auto-illustrated</strong>. When you finish the book, you have
            an illustrated manuscript instead of a wall of text — much easier to pitch, share, or read
            back.
          </p>
          <p>
            You can keep the dice off entirely for a pure novel workflow, or turn them on and let real
            random outcomes shape the story. Many writers do the second thing on purpose — the tension
            of a genuine bad roll is hard to fake by hand.
          </p>
          <p>
            When you&rsquo;re done, publish your custom world to the community library. Other writers
            can build inside it. You can read what they wrote.
          </p>
        </>
      )}
      features={FEATURES}
      faq={FAQ}
    />
  );
}
